import React from 'react';

import { Access } from 'src/shared/types';
import { NewProjectUserFormState, EditProjectUserFormState } from 'src/app/admin/views/user-management/state/userManagementSlice';
import ProjectPermissionsMatrixEditable from './components/project-permissions-matrix/ProjectPermissionsMatrixEditable';
import type { PersistUserProjectAccessPayload } from '../../userProjectAccessMutations';
import type { UserProjectAccess } from '../../state/userManagementSlice';

/** Props for the site-permissions matrix wrapper; `formState.projects` is the source of truth for checked roles */
export type AssignProjectProps = {
    formState: Pick<NewProjectUserFormState['formState'], 'projects' | 'email'> & {
        id?: number;
        status?: EditProjectUserFormState['formState']['status'];
        is_internal_user: boolean;
        cognitoId?: string;
        selectedUser?: NewProjectUserFormState['formState']['selectedUser'];
    };
    onFormChange: (fragment: Partial<NewProjectUserFormState['formState']>) => void;
    disabled?: boolean;
    formMode?: Access;
    /** When set, each cell toggle persists immediately; use Save changes for column header bulk toggles. */
    persistUserProjectAccess?: (payload: PersistUserProjectAccessPayload) => Promise<void>;
    /** Dims the permissions table and blocks interaction while a save is in flight (e.g. footer save or cell persist). */
    isSavingChanges?: boolean;
    /** After a successful server-side bulk role apply, sync persisted baseline / list caches (edit user flow). */
    onSitePermissionsBulkSynced?: (projects: UserProjectAccess[]) => void;
    /** When false, hides bulk assign/unassign-all (e.g. reassign-to-org: no server bulk; avoid heavy client bulk). Default true. */
    showBulkSiteRoleActions?: boolean;
};

/**
 * Entry from add/edit user flows: forwards props to `ProjectPermissionsMatrixEditable` and supplies `matrixSessionKey`
 * so matrix baselines reset when switching user (new vs edit vs different `selectedUser`).
 */
const AssignProjects = (props: AssignProjectProps) => {
    const { formState } = props;
    /** Stable id for “which user is being edited” — passed through so matrix internal refs reset on context change */
    const matrixSessionKey = `${formState.cognitoId ?? 'new'}-${formState.email || ''}-${formState.selectedUser?.cognitoId ?? ''}`;

    return <ProjectPermissionsMatrixEditable {...props} matrixSessionKey={matrixSessionKey} matrixTargetUserId={props.formState.id} />;
};

export default AssignProjects;
